import styled, { css } from "styled-components";
import { key } from "styled-theme";
import { breakpoints } from "root/libs/ui-styleguide/src";

const hoverButton = css`
  &:hover {
    outline: 1px solid transparent;
    border: 1px solid transparent;
  }
`;

const defaultButton = css`
  background-color: var(--dui-color-gray-400);
  border: var(--dui-size-width-border-xs) solid transparent;
  line-height: normal;
  text-align: center;
  text-decoration: ${(props) =>
    props.buttonType === "link" ? "underline" : "none"};
  min-height: auto;
  color: var(--dui-color-white-500);
  min-width: 210px;
  width: 100%;
  position: relative;
  font-weight: 700;
  padding: var(--gaq-size-space-9px) var(--gaq-size-space-5px);
  border-radius: var(--dui-size-radius-md);
  font-size: 1.4rem;

  @media screen and (min-width: ${breakpoints.tablet}) {
    font-size: ${key("fonts.desktop.copy.size")};
    ${(props) => (props.fluidButton ? "width: 100%;" : "width: auto;")};
  }

  ${hoverButton}
`;

const primaryButton = css`
  color: var(--dui-color-white-500);
  background-color: var(--dui-color-red-500);
  transition: ease-in-out 200ms background-color;

  &:focus {
    box-shadow: 0 0 0 4px white, 0 0 0 6px var(--dui-color-red-500);
  }

  /* Leave this after the focus style to prevent the box-shadow to appear on hover of a focused button */
  &:hover {
    background-color: var(--dui-color-red-400);
    box-shadow: unset;
  }

  &:disabled {
    color: var(--dui-color-white-500);
    border: var(--dui-size-width-border-xs) solid transparent;
    background-color: var(--dui-color-gray-300);
  }
`;

const secondaryButton = css`
  background-color: var(--dui-color-white-500);
  color: var(--dui-color-red-500);
  border: var(--dui-size-width-border-xs) solid var(--dui-color-red-500);

  &:focus,
  &:active {
    box-shadow: 0 0 0 4px white, 0 0 0 6px var(--dui-color-red-500);
    border-width: var(--dui-size-width-border-sm);
    padding: calc(9px - 1px) calc(5px - 1px);
  }

  /* Leave this after the focus style to prevent the box-shadow to appear on hover of a focused button */
  &:hover {
    border-color: var(--dui-color-red-400);
    color: var(--dui-color-red-400);
    box-shadow: unset;
  }

  &:disabled {
    background-color: var(--dui-color-white-500);
    color: var(--dui-color-gray-400);
    border-color: var(--dui-color-gray-300);
  }
`;

const successButton = css`
  background-color: var(--dui-color-positive-700);

  &:focus {
    box-shadow: 0 0 0 4px white, 0 0 0 6px var(--dui-color-positive-700);
    border-width: var(--dui-size-width-border-sm);
  }

  /* Leave this after the focus style to prevent the box-shadow to appear on hover of a focused button */
  &:hover {
    background-color: #029b48;
    box-shadow: unset;
  }
`;

const inlineStyles = css`
  font-weight: 700;
  font-size: ${key("fonts.mobile.copy.fontSize")};
  line-height: ${key("fonts.mobile.copy.lineHeight")};
  text-decoration: underline;

  &:focus {
    padding: var(--dui-size-space-1) var(--gaq-size-space-3px)
      var(--dui-size-space-1);
  }
`;

export const StyledLink = styled.a`
  color: inherit;
  text-decoration: none;

  &.inline {
    ${inlineStyles}
    color: var(--dui-color-red-500);

    &:focus {
      background-color: var(--dui-color-red-500);
      color: var(--dui-color-white-500);
    }
  }

  &.inline-black {
    ${inlineStyles}

    &:focus {
      background-color: var(--dui-color-black-500);
      color: var(--dui-color-white-500);
    }
  }

  &.primary {
    color: var(--dui-color-red-500);

    &:focus {
      text-decoration: underline;
    }
  }

  &.cta {
    width: 100%;
    display: block;
    ${defaultButton}
    ${(props) =>
      props.disableMinWidth ? "min-width: unset;" : ""}

    &:focus {
      text-decoration: none;
    }
  }

  &.cta.success {
    ${successButton}
  }

  &.cta.primary {
    ${primaryButton}
  }

  &.cta.secondary {
    ${secondaryButton}
  }
`;
